import React, { useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { CopyIcon, CheckIcon } from "@phosphor-icons/react";
import { colors, spacing } from "../../../../styles/design-system";
import useCopyContent from "../../../../hooks/useCopyContent";
import { ReadAloudPlaybackControl, ReadAloudIndicator } from "./ReadAloudPlaybackControl";
import type { ReadAloudControl } from "./ReadAloudPlaybackControl";
import { AnimatePresence, MotiView } from "moti";
import { ThumbsUpIcon, ThumbsDownIcon } from "@phosphor-icons/react";
import { MessageActionButton } from "./MessageActionButton";

const READ_ALOUD_WORDS_PER_MINUTE = 150;
const READ_ALOUD_MIN_SECONDS = 3;

const estimateTotalSeconds = (content: string) => {
  const words = content.trim().split(/\s+/).filter(Boolean).length;
  const estimated = Math.ceil(words / (READ_ALOUD_WORDS_PER_MINUTE / 60));
  return Math.max(READ_ALOUD_MIN_SECONDS, estimated || READ_ALOUD_MIN_SECONDS);
};

export interface MessageActionBarProps {
  content: string;
  onFeedback?: (value: "up" | "down" | null) => void;
  feedback?: "up" | "down";
  readAloudControl?: ReadAloudControl;
  hideReadAloudControl?: boolean;
}

export const MessageActionBar = ({
  content,
  onFeedback,
  feedback,
  readAloudControl,
  hideReadAloudControl = false,
}: MessageActionBarProps) => {
  const [showCheckMark, setShowCheckMark] = useCopyContent() as [boolean, (value: boolean) => void];

  const isReadingAloud = Boolean(readAloudControl?.isActive) || readAloudControl?.status === "loading";

  const totalSeconds = useMemo(
    () => readAloudControl?.totalSeconds ?? estimateTotalSeconds(content),
    [content, readAloudControl?.totalSeconds]
  );

  const handleVotePress = (value: "up" | "down") => {
    onFeedback?.(feedback === value ? null : value);
  };

  const handleCopyPress = () => {
    navigator.clipboard.writeText(content);
    setShowCheckMark(true);
  };

  return (
    <View style={styles.container}>
      <AnimatePresence>
        {readAloudControl && !hideReadAloudControl && isReadingAloud && (
          <MotiView
            style={styles.readAloudIndicatorWrap}
            from={{ opacity: 0, translateY: 8, scale: 0.98 }}
            animate={{ opacity: 1, translateY: 0, scale: 1 }}
            exit={{ opacity: 0, translateY: 6, scale: 0.98 }}
            transition={{ type: "timing", duration: 180 }}>
            <ReadAloudIndicator
              audioLevel={readAloudControl.audioLevel}
              audioLevels={readAloudControl.audioLevels}
              elapsedSeconds={readAloudControl.elapsedSeconds}
              totalSeconds={totalSeconds}
              status={readAloudControl.status}
              onPause={readAloudControl.onPause}
              onResume={readAloudControl.onResume}
              showTimer={readAloudControl.showTimer ?? true}
            />
          </MotiView>
        )}
      </AnimatePresence>
      <View style={styles.actionBar}>
        {onFeedback != null && (
          <>
            <MessageActionButton
              tooltipContent="Helpful"
              accessibilityLabel="Helpful"
              onPress={() => handleVotePress("up")}
              isActive={feedback === "up"}
              isSelected={feedback === "up"}
              activeStyle={styles.upvoteActive}
              activeHoverStyle={styles.upvoteActiveHover}
              icon={(isHovered) => (
                <ThumbsUpIcon
                  size={16}
                  weight={feedback === "up" ? "fill" : "regular"}
                  color={feedback === "up" ? colors.brand[600] : isHovered ? colors.grey[700] : colors.grey[400]}
                />
              )}
            />
            <MessageActionButton
              tooltipContent="Not helpful"
              accessibilityLabel="Not helpful"
              onPress={() => handleVotePress("down")}
              isActive={feedback === "down"}
              isSelected={feedback === "down"}
              activeStyle={styles.downvoteActive}
              activeHoverStyle={styles.downvoteActiveHover}
              icon={(isHovered) => (
                <ThumbsDownIcon
                  size={16}
                  weight={feedback === "down" ? "fill" : "regular"}
                  color={feedback === "down" ? colors.error[600] : isHovered ? colors.grey[700] : colors.grey[400]}
                />
              )}
            />
          </>
        )}

        <MessageActionButton
          tooltipContent={showCheckMark ? "Message copied" : "Copy"}
          accessibilityLabel={showCheckMark ? "Message copied" : "Copy message"}
          accessibilityHint={!showCheckMark ? "Press to copy the message to clipboard" : undefined}
          onPress={handleCopyPress}
          isDisabled={showCheckMark}
          isActive={showCheckMark}
          activeStyle={styles.copyActive}
          icon={(isHovered) =>
            showCheckMark ? (
              <CheckIcon size={16} color={colors.grey[500]} alt="Message copied" role="img" />
            ) : (
              <CopyIcon size={16} color={isHovered ? colors.grey[700] : colors.grey[400]} />
            )
          }
        />

        {readAloudControl && !hideReadAloudControl && <ReadAloudPlaybackControl readAloudControl={readAloudControl} />}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
  },
  readAloudIndicatorWrap: {
    alignSelf: "flex-start",
  },
  actionBar: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: spacing.xs,
    marginTop: spacing.xxs,
    gap: spacing.xs,
  },
  copyActive: {
    backgroundColor: colors.grey[100],
  },
  upvoteActive: {
    backgroundColor: colors.brand[50],
  },
  upvoteActiveHover: {
    backgroundColor: colors.brand[100],
  },
  downvoteActive: {
    backgroundColor: colors.error[100],
  },
  downvoteActiveHover: {
    backgroundColor: colors.error[200],
  },
});
